import React, { useEffect, useMemo, useState } from 'react';
import './ItemBundles.css';
import {
  FaPlus, FaEdit, FaTrash, FaUndo, FaSearch, FaTimes
} from 'react-icons/fa';

const API = import.meta.env.VITE_API_URL;

/* ===== role helpers (same pattern you use elsewhere) ===== */
function getCurrentUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}') || {}; }
  catch { return {}; }
}
function isClientUser(u) {
  const v = u.isclient ?? u.isClient ?? u.role ?? u.roleid;
  if (v === true) return true;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'y' || s === 'client';
  }
  return false;
}
function getUserClientId(u) { return (u.clientid ?? u.clientId ?? u.client_id ?? '') ? String(u.clientid ?? u.clientId ?? u.client_id) : ''; }
function getUserClientName(u) { return u.clientname ?? u.clientName ?? u.client ?? ''; }

const emptyBundle = {
  id: null,
  clientid: '',
  sku: '',
  name: '',
  description: '',
  components: [], // [{ itemid, sku, description, qty }]
};

export default function ItemBundles() {
  // user / role
  const USER = getCurrentUser();
  const IS_CLIENT = isClientUser(USER);
  const LOCKED_CLIENT_ID = getUserClientId(USER);
  const LOCKED_CLIENT_NAME = getUserClientName(USER);

  // client selection
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(() => {
    if (IS_CLIENT && LOCKED_CLIENT_ID) return LOCKED_CLIENT_ID;
    return localStorage.getItem('bundlesSelectedClientId') || '';
  });
  const currentClientId = IS_CLIENT ? LOCKED_CLIENT_ID : selectedClientId;

  // search/filter
  const [q, setQ] = useState(() => localStorage.getItem('bundlesQ') || '');
  const [showDeleted, setShowDeleted] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bundlesShowDeleted') || 'false'); } catch { return false; }
  });

  // data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // editor modal
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState(emptyBundle);
  const [saving, setSaving] = useState(false);

  // component SKU search
  const [skuSearch, setSkuSearch] = useState('');
  const [skuResults, setSkuResults] = useState([]);

  /* ========== effects ========== */
  useEffect(() => {
    fetchClientOptions();
    // initial load if a client is already selected/locked
    if (currentClientId) fetchBundles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { localStorage.setItem('bundlesSelectedClientId', currentClientId || ''); }, [currentClientId]);
  useEffect(() => { localStorage.setItem('bundlesQ', q); }, [q]);
  useEffect(() => { localStorage.setItem('bundlesShowDeleted', JSON.stringify(showDeleted)); }, [showDeleted]);

  useEffect(() => {
    const t = setTimeout(() => { if (editorOpen) runSkuSearch(); }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuSearch, draft.clientid, editorOpen]);

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const subtitle = rows?.total > 0 ? `(${Number(rows.total).toLocaleString()} found)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Bundles ', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [rows?.total]);

  /* ========== API helpers ========== */
  const fetchJSON = async (url, opts) => {
    const res = await fetch(url, opts);
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try { const b = await res.json(); if (b?.error) msg = b.error; } catch {}
      throw new Error(msg);
    }
    return res.json();
  };

  const fetchClientOptions = async () => {
    if (IS_CLIENT && LOCKED_CLIENT_ID) {
      setClientOptions([{ id: String(LOCKED_CLIENT_ID), name: (LOCKED_CLIENT_NAME || `Client #${LOCKED_CLIENT_ID}`) }]);
      return;
    }
    try {
      const json = await fetchJSON(`${API}/api/clients?limit=1000`);
      const opts = Array.isArray(json.data) ? json.data.map(c => ({ id: String(c.id), name: c.name })) : [];
      setClientOptions(opts);
    } catch { setClientOptions([]); }
  };

  const fetchBundles = async () => {
    if (!currentClientId) { setRows([]); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ clientid: currentClientId });
      if (q) params.set('q', q);
      if (showDeleted) params.set('deleted', 1);
      const json = await fetchJSON(`${API}/api/items/bundles?${params.toString()}`);
      setRows(Array.isArray(json?.data) ? json.data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadBundle = async (id) => {
    const data = await fetchJSON(`${API}/api/items/bundles/${id}`);
    const comp = Array.isArray(data?.components) ? data.components : [];
    setDraft({
      id: data.id,
      clientid: data.clientid,
      sku: data.sku || '',
      name: data.name || '',
      description: data.description || '',
      components: comp.map(c => ({ itemid: c.itemid, sku: c.sku, description: c.description || '', qty: c.qty || 1 })),
    });
  };

  /* ========== actions ========== */
  const onNew = () => {
    setDraft({ ...emptyBundle, clientid: currentClientId || '' });
    setEditorOpen(true);
    setSkuResults([]);
    setSkuSearch('');
  };

  const onEdit = async (row) => {
    try {
      await loadBundle(row.id);
      setEditorOpen(true);
      setSkuResults([]);
      setSkuSearch('');
    } catch (e) { alert(e.message); }
  };

  const onDelete = async (row) => {
    if (!window.confirm('Delete this bundle?')) return;
    try {
      await fetchJSON(`${API}/api/items/bundles/${row.id}`, { method: 'DELETE' });
      fetchBundles();
    } catch (e) { alert(e.message); }
  };

  const onRestore = async (row) => {
    try {
      await fetchJSON(`${API}/api/items/bundles/${row.id}/restore`, { method: 'PUT' });
      fetchBundles();
    } catch (e) { alert(e.message); }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {
        clientid: draft.clientid,
        sku: String(draft.sku || '').trim(),
        name: String(draft.name || '').trim(),
        description: draft.description || '',
        components: (draft.components || [])
          .filter(c => c.itemid && (c.qty || 0) > 0)
          .map(c => ({ itemid: c.itemid, qty: Number(c.qty) || 1 })),
      };
      if (!payload.clientid) throw new Error('Client is required');
      if (!payload.sku)      throw new Error('Bundle SKU is required');
      if (!payload.name)     throw new Error('Bundle Name is required');
      if (!payload.components.length) throw new Error('Add at least one component');

      if (draft.id) {
        await fetchJSON(`${API}/api/items/bundles/${draft.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });
      } else {
        await fetchJSON(`${API}/api/items/bundles`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });
      }
      setEditorOpen(false);
      fetchBundles();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  /* ========== component picker ========== */
  const runSkuSearch = async () => {
    const term = skuSearch.trim();
    if (!editorOpen || !term || !draft.clientid) { setSkuResults([]); return; }
    try {
      const url = `${API}/api/items/lookupbysku?clientid=${encodeURIComponent(draft.clientid)}&sku=${encodeURIComponent(term)}`;
      const rows = await fetchJSON(url);
      setSkuResults(rows || []);
    } catch {
      setSkuResults([]);
    }
  };
  const addComponent = (item) => {
    if (!item?.itemid) return;
    setDraft(d => {
      const exists = (d.components || []).some(c => String(c.itemid) === String(item.itemid));
      if (exists) return d;
      return {
        ...d,
        components: [...(d.components || []), { itemid: item.itemid, sku: item.sku, description: item.description || '', qty: 1 }]
      };
    });
    setSkuSearch('');
    setSkuResults([]);
  };
  const setComponentQty = (idx, qty) => {
    setDraft(d => {
      const list = [...(d.components || [])];
      list[idx] = { ...list[idx], qty: Math.max(1, parseInt(qty || '1', 10) || 1) };
      return { ...d, components: list };
    });
  };
  const removeComponent = (idx) => {
    setDraft(d => ({ ...d, components: (d.components || []).filter((_, i) => i !== idx) }));
  };

  /* ========== derived ========== */
  const clientName = (id) => clientOptions.find(c => String(c.id) === String(id))?.name || id;

  const toolbarClient = (
    IS_CLIENT ? (
      <div className="client-pill" title="Your account">
        Client: <strong>{LOCKED_CLIENT_NAME || LOCKED_CLIENT_ID || 'My Company'}</strong>
      </div>
    ) : (
      <div className="client-select">
        <label htmlFor="bundles-client-select" style={{ marginRight: 6 }}>Client</label>
        <select
          id="bundles-client-select"
          value={selectedClientId || ''}
          onChange={(e) => {
            const val = e.target.value;
            setSelectedClientId(val);
            if (val) fetchBundles();
            else setRows([]);
          }}
          className="client-dropdown"
          required
        >
          <option value="" disabled>— Choose client —</option>
          {clientOptions.map(opt => (
            <option key={opt.id} value={opt.id}>{opt.name}</option>
          ))}
        </select>
        {selectedClientId && (
          <button className="clear" title="Clear client" onClick={() => { setSelectedClientId(''); setRows([]); }}>
            <FaTimes size={14}/>
          </button>
        )}
      </div>
    )
  );

  /* ========== render ========== */
  return (
    <div className="bundles-wrapper">
      <div className="bundles-header">
        <div className="bundles-right">{toolbarClient}</div>
      </div>

      <div className="bundles-toolbar-row">
        <div className="search">
          <FaSearch />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search bundle SKU or name"
            onKeyDown={(e) => { if (e.key === 'Enter' && currentClientId) fetchBundles(); }}
          />
          {q && (
            <button className="clear" onClick={() => { setQ(''); if (currentClientId) fetchBundles(); }}>
              <FaTimes />
            </button>
          )}
        </div>

        <label className="checkbox-inline">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={e => { setShowDeleted(e.target.checked); if (currentClientId) fetchBundles(); }}
          />
          Show deleted
        </label>

        <button className="btn" onClick={() => currentClientId && fetchBundles()} disabled={!currentClientId}>
          Search
        </button>

        {!IS_CLIENT && (
          <button className="btn primary" onClick={onNew} disabled={!currentClientId}>
            <FaPlus /> New Bundle
          </button>
        )}
      </div>

      <div className="bundles-tablecol">
        {loading ? (
          <div className="loading">Loading…</div>
        ) : (
          <table className="bundles-table">
            <thead>
              <tr>
                <th style={{ width: 1, whiteSpace: 'nowrap' }}>Actions</th>
                <th>Bundle SKU</th>
                <th>Name</th>
                <th>Components</th>
                <th>Client</th>
                <th style={{ width: 1 }}>Deleted</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map(b => (
                <tr key={b.id} className={b.deleted ? 'deleted-row' : ''}>
                  <td className="actions">
                    {!b.deleted ? (
                      <>
                        {!IS_CLIENT && (
                          <button className="mini-btn" title="Edit" onClick={() => onEdit(b)}>
                            <FaEdit size={14}/>
                          </button>
                        )}
                        {!IS_CLIENT && (
                          <button className="mini-btn danger" title="Delete" onClick={() => onDelete(b)}>
                            <FaTrash size={14}/>
                          </button>
                        )}
                      </>
                    ) : (
                      !IS_CLIENT && (
                        <button className="mini-btn" title="Restore" style={{ color: 'green' }} onClick={() => onRestore(b)}>
                          <FaUndo size={14}/>
                        </button>
                      )
                    )}
                  </td>
                  <td>{b.sku}</td>
                  <td>{b.name}</td>
                  <td className="components-col">{b.components_summary || ''}</td>
                  <td>{clientName(b.clientid)}</td>
                  <td style={{ textAlign: 'center' }}>{b.deleted ? 'Yes' : 'No'}</td>
                </tr>
              )) : (
                <tr><td colSpan={6} style={{ color: '#aaa' }}>{currentClientId ? 'No bundles' : 'Choose a client to view bundles'}</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Editor Modal */}
      {editorOpen && (
        <div className="modal-backdrop" onClick={() => setEditorOpen(false)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bundleEditorTitle"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 820, width: '95vw' }}
          >
            <div className="modal-header">
              <h3 id="bundleEditorTitle">{draft.id ? `Edit Bundle #${draft.id}` : 'New Bundle'}</h3>
              <button className="icon-btn" onClick={() => setEditorOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="bundle-form">
                <label>
                  Client
                  <select
                    value={draft.clientid || ''}
                    onChange={e => setDraft(d => ({ ...d, clientid: e.target.value }))}
                    disabled={IS_CLIENT}
                    required
                  >
                    {IS_CLIENT ? (
                      <option value={LOCKED_CLIENT_ID}>{LOCKED_CLIENT_NAME || LOCKED_CLIENT_ID}</option>
                    ) : (
                      <>
                        <option value="">— Select —</option>
                        {clientOptions.map(opt => (
                          <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                      </>
                    )}
                  </select>
                </label>
                <label>
                  Bundle SKU
                  <input
                    value={draft.sku}
                    onChange={e => setDraft(d => ({ ...d, sku: e.target.value }))}
                    placeholder="e.g. PACK-COZY-WINTER"
                    required
                  />
                </label>
                <label>
                  Name
                  <input
                    value={draft.name}
                    onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                    placeholder="Cozy Winter Pack"
                    required
                  />
                </label>
                <label className="span-2">
                  Description
                  <textarea
                    value={draft.description || ''}
                    onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
                    rows={3}
                  />
                </label>
              </div>

              <div className="bundle-components">
                <div className="comp-toolbar">
                  <div className="search bundle-search">
                    <FaSearch />
                    <input
                      value={skuSearch}
                      onChange={e => setSkuSearch(e.target.value)}
                      placeholder="Search SKU to add"
                    />
                    {skuSearch && <button className="clear" onClick={()=>setSkuSearch('')}><FaTimes/></button>}
                  </div>
                  <div className="comp-hint">Add SKUs from the selected client. Set quantities per component.</div>
                </div>

                {skuResults.length > 0 && (
                  <div className="sku-results">
                    {skuResults.slice(0, 8).map(r => (
                      <button key={r.itemid} className="chip" onClick={() => addComponent(r)} title={r.description || ''}>
                        {r.sku}
                      </button>
                    ))}
                  </div>
                )}

                <table className="table comp-table">
                  <thead>
                    <tr>
                      <th style={{ width: 1 }}></th>
                      <th>SKU</th>
                      <th>Description</th>
                      <th style={{ width: 100 }}>Qty</th>
                      <th style={{ width: 1 }}>Remove</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(draft.components || []).length ? (
                      draft.components.map((c, idx) => (
                        <tr key={c.itemid || idx}>
                          <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                          <td>{c.sku}</td>
                          <td>{c.description || ''}</td>
                          <td>
                            <input
                              type="number"
                              min="1"
                              value={c.qty}
                              onChange={e => setComponentQty(idx, e.target.value)}
                              style={{ width: 80 }}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button className="mini-btn danger" title="Remove" onClick={() => removeComponent(idx)}>
                              <FaTrash size={14}/>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={5} style={{ color: '#aaa' }}>No components added</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn" onClick={() => setEditorOpen(false)}>Cancel</button>
              <button className="btn primary" onClick={onSave} disabled={saving}>
                {saving ? 'Saving…' : (draft.id ? 'Save Changes' : 'Create Bundle')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
